import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

interface BookingPackageServiceItem {
  serviceId: string;
  serviceName: string;
  serviceNameEn: string;
  startTime: string;
  endTime: string;
  duration: number;
}

interface BookingCartItemBase {
  cartItemId: string;
  type: 'service' | 'package';
  id: string;
  name: string;
  nameEn?: string; // English name for GTM data consistency
  price: number;
  duration: number;
  quantity: number;
}

export interface BookingCartServiceItem extends BookingCartItemBase {
  type: 'service';
  startTime: string;
  endTime: string;
  invitees: string[];
  services?: never;
}

export interface BookingCartPackageItem extends BookingCartItemBase {
  type: 'package';
  startTime: string;
  endTime: string;
  services: BookingPackageServiceItem[];
  invitees?: never;
}

export type BookingCartItem = BookingCartServiceItem | BookingCartPackageItem;

type NewBookingCartItem =
  | Omit<BookingCartServiceItem, 'cartItemId' | 'quantity'>
  | Omit<BookingCartPackageItem, 'cartItemId' | 'quantity'>;

interface BookingCartState {
  items: BookingCartItem[];
  addBooking: (item: NewBookingCartItem) => void;
  removeBooking: (cartItemId: string) => void;
  updateBooking: (
    cartItemId: string,
    newDetails:
      | Partial<Omit<BookingCartServiceItem, 'cartItemId'>>
      | Partial<Omit<BookingCartPackageItem, 'cartItemId'>>
  ) => void;
  clearCart: () => void;
}

const mergePackageServices = (
  existingServices: BookingPackageServiceItem[],
  incomingServices: BookingPackageServiceItem[]
): BookingPackageServiceItem[] => {
  const mergedByServiceId = new Map(
    existingServices.map((service) => [service.serviceId, service])
  );

  incomingServices.forEach((service) => {
    mergedByServiceId.set(service.serviceId, service);
  });

  return Array.from(mergedByServiceId.values());
};

const filterPastBookings = (items: BookingCartItem[]): BookingCartItem[] => {
  const now = new Date();
  return items.filter((item) => new Date(item.startTime) > now);
};

export const useBookingCartStore = create<BookingCartState>()(
  persist(
    (set) => ({
      items: [],
      addBooking: (item) =>
        set((state) => ({
          items: [
            ...state.items,
            item.type === 'service'
              ? {
                  ...item,
                  cartItemId: uuidv4(),
                  quantity: 1,
                  invitees: item.invitees ?? [],
                }
              : {
                  ...item,
                  cartItemId: uuidv4(),
                  quantity: 1,
                  services: item.services ?? [],
                },
          ],
        })),
      removeBooking: (cartItemId) =>
        set((state) => ({
          items: state.items.filter((item) => item.cartItemId !== cartItemId),
        })),
      updateBooking: (cartItemId, newDetails) => {
        set((state) => ({
          items: state.items.map(function (item) {
            if (item.cartItemId === cartItemId) {
              if (item.type === 'package') {
                const packageDetails = newDetails as Partial<
                  Omit<BookingCartPackageItem, 'cartItemId'>
                >;
                return {
                  ...item,
                  cartItemId,
                  ...packageDetails,
                  services: packageDetails.services
                    ? mergePackageServices(item.services, packageDetails.services)
                    : item.services,
                };
              }

              const serviceDetails = newDetails as Partial<
                Omit<BookingCartServiceItem, 'cartItemId'>
              >;
              return { ...item, ...serviceDetails, cartItemId };
            }

            return item;
          }),
        }));
      },
      clearCart: () => set({ items: [] }),
    }),
    {
      name: 'booking-cart-storage',
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.items = filterPastBookings(state.items);
        }
      },
    }
  )
);
